import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Steps, Form } from 'antd';
import PropTypes from 'prop-types';
import { useAuthContext } from '../../contexts/AuthContext';

import {
  ContentCustom,
  CreateUpdateContainer,
  PageHeaderCustom
} from '../../components';
import { MainInformationsForm } from './components/forms/MainInformations';
import { SupervisionAddressForm } from './components/forms/SupervisionAddress';
import { SummaryForm } from './components/forms/Summary';
import { useSupervisionForm } from './hooks/useSupervisionForm';
import { useSupervisionSteps } from './hooks/useSupervisionSteps';
import { useSupervisionProfileLock } from './hooks/useSupervisionProfileLock';

export const CreateUpdateSupervision = ({ purpose }) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const location = useLocation();
  const [current, setCurrent] = useState(0);
  const { t } = useTranslation();
  const {
    user: { role }
  } = useAuthContext();
  // Extract owner ID from URL params
  const searchParams = new URLSearchParams(location.search);
  const OwnerId = searchParams.get('id');

  // Custom hooks for managing different aspects of the component
  const { refs, states, config, handleNextStep, setOwner, setOwnerId } =
    useSupervisionForm({
      form,
      purpose,
      OwnerId,
      setCurrent,
      current
    });

  const { items } = useSupervisionSteps();

  // Handle profile locking for edit mode
  useSupervisionProfileLock({ purpose, id });

  // Forms configuration
  const forms = [
    <MainInformationsForm
      key="main-info"
      handleNextStep={handleNextStep}
      animalsRef={refs.animalsRef}
      form={form}
      startDate={refs.startDateRef}
      endDate={refs.endDateRef}
    />,
    <SupervisionAddressForm
      key="address"
      handleNextStep={handleNextStep}
      handlePrevious={states.handlePrevious}
      form={form}
      country={states.country}
      setCountry={states.setCountry}
    />,
    <SummaryForm
      key="summary"
      handleNextStep={handleNextStep}
      handlePrevious={states.handlePrevious}
      form={form}
      duration={states.duration}
      setDuration={states.setDuration}
      differenceDays={states.differenceDays}
      animalsFileList={states.animalsFileList}
      selectedAnouncePhotos={states.selectedAnouncePhotos}
      setSelectedAnouncePhotos={states.setSelectedAnouncePhotos}
      reduction={states.reduction}
      setReduction={states.setReduction}
      purpose={purpose}
    />
  ];

  useEffect(
    () => () => {
      if (role === 'users:ADMIN') {
        form.resetFields();
        setOwner({});
        setOwnerId(null);
      }
    },
    [form]
  );

  return (
    <>
      <PageHeaderCustom title={t('supervisions.form.title.create')} />
      <ContentCustom>
        <Steps current={current} items={items} style={{ marginBottom: 24 }} />
        <CreateUpdateContainer
          purpose={purpose}
          customFormInstance={form}
          baseUrl="supervisions/form"
          resource="supervisions"
          config={config}
          formExtra={forms[current]}
          loadingFields={states.isFieldsLoading}
          withFooterButtons={false}
        />
      </ContentCustom>
    </>
  );
};

CreateUpdateSupervision.propTypes = {
  purpose: PropTypes.string.isRequired
};
